import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { articleImages } from '@/mocks/images';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const imageUrl = articleImages[article.imageSeq] || articleImages['article-1-image'];

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={imageUrl}
            alt={article.title}
            title={`${article.title} - 心灵家缘`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block px-2.5 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-2 whitespace-nowrap">
            {article.categoryName}
          </span>
          <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1.5">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span className="whitespace-nowrap">{article.author}</span>
            <span className="whitespace-nowrap">{article.date}</span>
            <span className="whitespace-nowrap">{article.readTime}阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={imageUrl}
            alt={article.title}
            title={`${article.title} - 心灵家缘`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h3>
          <div className="flex items-center gap-2 text-xs text-foreground-400">
            <span className="whitespace-nowrap">{article.date}</span>
            <span className="whitespace-nowrap">{article.readTime}阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 overflow-hidden cursor-pointer"
    >
      <div className="w-full h-48 overflow-hidden bg-background-200">
        <img
          src={imageUrl}
          alt={article.title}
          title={`${article.title} - 心灵家缘`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <span className="inline-block px-2.5 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-3 w-fit whitespace-nowrap">
          {article.categoryName}
        </span>
        <h3 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3 flex-1">{article.excerpt}</p>
        <div className="flex items-center gap-3 text-xs text-foreground-400 pt-3 border-t border-background-100">
          <span className="whitespace-nowrap">{article.author}</span>
          <span className="whitespace-nowrap">{article.date}</span>
          <span className="ml-auto whitespace-nowrap">{article.readTime}阅读</span>
        </div>
      </div>
    </Link>
  );
}